import React from "react";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Trash2 } from "lucide-react";

export const TAX_OPTIONS = [
  { value: "NONE", label: "None", rate: 0 },
  { value: "GST_5", label: "GST 5%", rate: 5 },
  { value: "GST_12", label: "GST 12%", rate: 12 },
  { value: "GST_18", label: "GST 18%", rate: 18 },
  { value: "GST_28", label: "GST 28%", rate: 28 },
];

export const makeEmptyLine = () => ({
  rm_id: "",
  rm_search: "",
  description: "",
  hsn: "",
  unit: "",
  quantity: 1,
  rate: 0,
  tax: "GST_18",
  tax_id: "",
  amount: 0,
  account_id: "",
  // Optional per-item cap (from `available-items` API for job-work
  // invoices; RM Inward leaves it undefined).
  available_qty: undefined,
});

// Shared line-items table (Feb 2026). Matches RM Inward field list +
// interaction pattern verbatim; the only variable is `items` (the
// picker's underlying dataset).
//
// Props:
//   lineItems           — array of line rows
//   onLineItemsChange   — (nextArray) => void
//   items               — array of {rm_id, description, unit, hsn, available_qty?}
//                         from either the full RM catalog (RM Inward) or the
//                         vendor's uninvoiced-jobwork-items endpoint.
//   itemSearchLabel     — column header label (default "Item Details")
//   emptyItemsHint      — helper text under an empty item picker
//                         (e.g. "Select vendor to load uninvoiced items")
//   zohoTaxes           — Zoho tax rows (falls back to TAX_OPTIONS when empty)
//   zohoAccounts        — Zoho chart-of-accounts rows (hides column when empty and
//                         `showAccountColumn={false}`)
//   showAccountColumn   — force show/hide the Account picker column
//   defaultAccountId    — pre-selected account id per line
//   readOnly            — disables all edits (view-only)
//   capQtyToAvailable   — when true, `quantity` inputs are hard-capped at
//                         `item.available_qty` and rows for items without an
//                         `available_qty` field are treated as uncapped.
export const BillLineItemsTable = ({
  lineItems,
  onLineItemsChange,
  items,
  itemSearchLabel = "Item Details",
  emptyItemsHint = null,
  zohoTaxes = [],
  zohoAccounts = [],
  showAccountColumn = true,
  defaultAccountId = "",
  readOnly = false,
  capQtyToAvailable = false,
}) => {
  const zohoConfigured = showAccountColumn;
  const accountsDropdown = zohoConfigured && zohoAccounts.length > 0;

  const setLine = (idx, patch) => {
    const next = [...lineItems];
    next[idx] = { ...next[idx], ...patch };
    onLineItemsChange(next);
  };

  const handleRMSelect = (idx, rmId) => {
    const item = items.find((it) => it.rm_id === rmId);
    if (!item) {
      setLine(idx, { rm_id: rmId, rm_search: rmId });
      return;
    }
    const patch = {
      rm_id: item.rm_id,
      rm_search: item.rm_id,
      description: item.description || "",
      hsn: item.hsn || "",
      unit: item.unit || "",
      available_qty: typeof item.available_qty === "number" ? item.available_qty : undefined,
      account_id: defaultAccountId || "",
    };
    // Cap the initial qty at available_qty (job-work path only).
    if (capQtyToAvailable && typeof item.available_qty === "number") {
      const cur = Number(lineItems[idx]?.quantity || 1);
      patch.quantity = Math.min(cur > 0 ? cur : 1, item.available_qty);
    }
    setLine(idx, patch);
  };

  const handleQtyChange = (idx, raw) => {
    let q = parseFloat(raw) || 0;
    if (capQtyToAvailable) {
      const cap = lineItems[idx]?.available_qty;
      if (typeof cap === "number" && q > cap) q = cap;
    }
    if (q < 0) q = 0;
    const rate = Number(lineItems[idx]?.rate || 0);
    setLine(idx, { quantity: q, amount: q * rate });
  };

  const handleRateChange = (idx, raw) => {
    const r = parseFloat(raw) || 0;
    const q = Number(lineItems[idx]?.quantity || 0);
    setLine(idx, { rate: r, amount: q * r });
  };

  const handleRemove = (idx) => {
    if (lineItems.length === 1) return;
    onLineItemsChange(lineItems.filter((_, i) => i !== idx));
  };

  const handleAddLine = () => {
    onLineItemsChange([...lineItems, makeEmptyLine()]);
  };

  // Build a per-line datalist that excludes already-picked rm_ids on
  // OTHER rows — same finished part shouldn't appear twice on the same
  // invoice (server rejects it via the per-line available_qty check;
  // this is UX prevention).
  const usedRmIds = new Set(lineItems.map((li) => li.rm_id).filter(Boolean));
  const filterItems = (search, currentRmId) => {
    const q = (search || "").trim().toLowerCase();
    return items.filter((it) => {
      if (usedRmIds.has(it.rm_id) && it.rm_id !== currentRmId) return false;
      if (!q) return true;
      return (
        it.rm_id.toLowerCase().includes(q) ||
        (it.description || "").toLowerCase().includes(q)
      );
    });
  };

  const lastRow = lineItems[lineItems.length - 1];
  const showInlineAdd = !readOnly && lastRow && lastRow.rm_id;

  const colSpan = zohoConfigured ? 8 : 7;

  return (
    <div>
      <div className="flex justify-between items-center mb-2">
        <Label className="text-xs font-bold uppercase">{itemSearchLabel}</Label>
      </div>
      <div className="border rounded-lg overflow-hidden overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="bg-gray-50">
              <TableHead className="w-[240px]">Item Details</TableHead>
              <TableHead className="w-[110px] text-right">Qty</TableHead>
              <TableHead className="w-[70px]">Unit</TableHead>
              <TableHead className="w-[110px] text-right">Rate</TableHead>
              <TableHead className="w-[80px]">Tax</TableHead>
              {zohoConfigured && <TableHead className="w-[180px]">Account</TableHead>}
              <TableHead className="w-[100px] text-right">Amount</TableHead>
              <TableHead className="w-[40px]"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {lineItems.map((item, idx) => {
              const matches = filterItems(item.rm_search || "", item.rm_id);
              const overCap = capQtyToAvailable
                && typeof item.available_qty === "number"
                && Number(item.quantity || 0) > item.available_qty;
              return (
                <React.Fragment key={idx}>
                  <TableRow className="align-top">
                    <TableCell className="p-2 align-top">
                      <Input
                        value={item.rm_search || item.rm_id || ""}
                        onChange={(e) => setLine(idx, { rm_search: e.target.value, rm_id: "" })}
                        placeholder="Type item ID..."
                        className="font-mono text-xs h-8 w-[160px]"
                        list={`bill-items-${idx}`}
                        disabled={readOnly}
                        data-testid={`bill-line-${idx}-search`}
                      />
                      <datalist id={`bill-items-${idx}`}>
                        {matches.map((rm) => (
                          <option key={rm.rm_id} value={rm.rm_id}>
                            {rm.description}
                          </option>
                        ))}
                      </datalist>
                      {item.rm_search && !item.rm_id && (
                        <select
                          className="w-full text-xs border rounded px-1 py-1 bg-gray-50 mt-1"
                          value=""
                          onChange={(e) => handleRMSelect(idx, e.target.value)}
                          data-testid={`bill-line-${idx}-picker`}
                        >
                          <option value="">
                            {matches.length === 0
                              ? (emptyItemsHint || "No matches")
                              : "Select from matches"}
                          </option>
                          {matches.map((rm) => (
                            <option key={rm.rm_id} value={rm.rm_id}>
                              {rm.rm_id} — {rm.description}
                              {typeof rm.available_qty === "number"
                                ? ` (avail ${rm.available_qty})` : ""}
                            </option>
                          ))}
                        </select>
                      )}
                      {item.rm_id && (
                        <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
                          <span className="bg-blue-100 text-blue-700 text-[9px] font-bold px-1.5 py-0.5 rounded uppercase tracking-wide">
                            Item
                          </span>
                          {item.hsn && (
                            <span className="text-[11px] text-zinc-500">
                              HSN: <span className="text-zinc-700 font-mono">{item.hsn}</span>
                            </span>
                          )}
                          {typeof item.available_qty === "number" && (
                            <span
                              className={`text-[10px] font-mono px-1.5 py-0.5 rounded ${
                                overCap ? "bg-red-100 text-red-700" : "bg-emerald-50 text-emerald-700"
                              }`}
                              data-testid={`bill-line-${idx}-available`}
                            >
                              avail: {item.available_qty}
                            </span>
                          )}
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="p-2 align-top">
                      <Input
                        type="number"
                        min="0"
                        step="0.01"
                        value={item.quantity}
                        onChange={(e) => handleQtyChange(idx, e.target.value)}
                        onWheel={(e) => e.currentTarget.blur()}
                        className={`no-spinner text-right font-mono text-xs h-8 ${overCap ? "border-red-400" : ""}`}
                        disabled={readOnly}
                        data-testid={`bill-line-${idx}-qty`}
                      />
                    </TableCell>
                    <TableCell className="p-2 align-top">
                      <div
                        className={`text-xs font-mono h-8 flex items-center px-2 rounded border ${
                          item.unit
                            ? "bg-gray-50 text-gray-700 border-gray-200"
                            : "bg-red-50 text-red-600 border-red-200"
                        }`}
                        data-testid={`bill-line-${idx}-unit`}
                      >
                        {item.unit || "—"}
                      </div>
                    </TableCell>
                    <TableCell className="p-2 align-top">
                      <Input
                        type="number"
                        min="0"
                        step="0.01"
                        value={item.rate}
                        onChange={(e) => handleRateChange(idx, e.target.value)}
                        onWheel={(e) => e.currentTarget.blur()}
                        className="no-spinner text-right font-mono text-xs h-8"
                        placeholder="0.00"
                        disabled={readOnly}
                        data-testid={`bill-line-${idx}-rate`}
                      />
                    </TableCell>
                    <TableCell className="p-2 align-top">
                      {zohoTaxes.length > 0 ? (
                        <Select
                          value={item.tax_id || ""}
                          onValueChange={(v) => {
                            const tax = zohoTaxes.find((t) => t.tax_id === v);
                            setLine(idx, {
                              tax_id: v,
                              tax: tax?.tax_name || "",
                              tax_percent: parseFloat(tax?.tax_percentage) || 0,
                            });
                          }}
                        >
                          <SelectTrigger className="h-8 text-xs" data-testid={`bill-line-${idx}-tax`}>
                            <SelectValue placeholder="Tax" />
                          </SelectTrigger>
                          <SelectContent>
                            {zohoTaxes.map((t) => (
                              <SelectItem key={t.tax_id} value={t.tax_id}>
                                {t.tax_name} ({t.tax_percentage}%)
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      ) : (
                        <Select
                          value={item.tax || undefined}
                          onValueChange={(v) => setLine(idx, { tax: v })}
                        >
                          <SelectTrigger className="h-8 text-xs" data-testid={`bill-line-${idx}-tax`}>
                            <SelectValue placeholder="Tax" />
                          </SelectTrigger>
                          <SelectContent>
                            {TAX_OPTIONS.map((t) => (
                              <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}
                    </TableCell>
                    {zohoConfigured && (
                      <TableCell className="p-2 align-top">
                        {accountsDropdown ? (
                          <Select
                            value={item.account_id || defaultAccountId || undefined}
                            onValueChange={(v) => setLine(idx, { account_id: v })}
                          >
                            <SelectTrigger
                              className="min-h-[2.25rem] h-auto text-xs py-1.5 items-start [&>span]:whitespace-normal [&>span]:text-left [&>span]:leading-tight"
                              data-testid={`bill-line-${idx}-account`}
                            >
                              <SelectValue placeholder="Select account" />
                            </SelectTrigger>
                            <SelectContent>
                              {zohoAccounts.map((acc) => (
                                <SelectItem
                                  key={acc.account_id}
                                  value={acc.account_id}
                                  className="whitespace-normal"
                                >
                                  {acc.account_name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        ) : (
                          <Input
                            value={item.account_id || ""}
                            onChange={(e) => setLine(idx, { account_id: e.target.value })}
                            placeholder="Account ID"
                            className="text-xs h-8 font-mono"
                            data-testid={`bill-line-${idx}-account`}
                          />
                        )}
                      </TableCell>
                    )}
                    <TableCell className="p-2 align-top text-right font-mono text-sm font-medium">
                      ₹{Number(item.amount || 0).toFixed(2)}
                    </TableCell>
                    <TableCell className="p-2 align-top">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleRemove(idx)}
                        disabled={lineItems.length === 1 || readOnly}
                        className="h-8 w-8 p-0"
                        data-testid={`bill-line-${idx}-remove`}
                      >
                        <Trash2 className="h-4 w-4 text-red-500" />
                      </Button>
                    </TableCell>
                  </TableRow>
                  {item.rm_id && item.description && (
                    <TableRow className="hover:bg-transparent border-t-0">
                      <TableCell
                        colSpan={colSpan}
                        className="pt-0 pb-2 px-2 text-xs text-zinc-600 italic whitespace-normal break-words"
                        data-testid={`bill-line-${idx}-description`}
                      >
                        <span className="text-[10px] uppercase tracking-wider text-zinc-400 mr-1.5">
                          Description:
                        </span>
                        {item.description}
                      </TableCell>
                    </TableRow>
                  )}
                </React.Fragment>
              );
            })}
            {showInlineAdd && (
              <TableRow className="bg-zinc-50 hover:bg-zinc-100" data-testid="bill-inline-add-row">
                <TableCell colSpan={colSpan} className="p-2">
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={handleAddLine}
                    className="h-7 text-xs text-blue-700 hover:text-blue-900"
                    data-testid="bill-inline-add-btn"
                  >
                    <Plus className="h-3 w-3 mr-1" /> Add another line
                  </Button>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};

export default BillLineItemsTable;
